import React, { useLayoutEffect, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { useTranslation } from 'react-i18next';
import {
  convertHtmlToMarkdown,
  convertMarkdownToHtml,
} from '@wajeez-web/core/utils';

import { TinyMceInputV2Type } from './types';
import InputLabel from '../Inputs/InputLabel';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';

export const TinyMCEInputV2 = React.forwardRef<Editor, TinyMceInputV2Type>(
  (
    {
      id,
      value = '',
      disabled,
      height,
      label,
      onChange,
      required,
      placeholder,
      isError,
      fieldState,
    }: TinyMceInputV2Type,
    ref
  ) => {
    const { t } = useTranslation();

    const initialValue = useRef(convertMarkdownToHtml(value || '')).current;

    useLayoutEffect(() => {
      const style = document.createElement('style');

      style.innerHTML = `
      .tox .tox-edit-area::before {
        border: 2px solid ${!isError ? 'var(--theme-primary)' : '#EF4444'};
      }
    `;

      document.body.prepend(style);
    }, [isError]);

    return (
      <div className="relative w-full">
        {label && (
          <InputLabel text={label} name={id || ''} required={required} />
        )}
        <Editor
          licenseKey="gpl"
          tinymceScriptSrc={'assets/scripts/tinymce/tinymce.min.js'}
          ref={ref}
          value={initialValue}
          disabled={disabled || false}
          init={{
            placeholder: t(
              `inputs.placeholders.${placeholder}`,
              placeholder || ''
            ) as string,
            directionality: 'rtl',
            height: height || 250,
            menubar: false,
            invalid_elements: 'table,td,tr,th',
            text_patterns: [
              { start: '*', end: '*', format: 'italic' },
              { start: '**', end: '**', format: 'bold' },
              { start: '#', format: 'h1' },
              { start: '##', format: 'h2' },
              { start: '###', format: 'h3' },
              { start: '####', format: 'h4' },
              { start: '#####', format: 'h5' },
              { start: '######', format: 'h6' },
              // The following text patterns require the `lists` plugin
              { start: '1. ', cmd: 'InsertOrderedList' },
              { start: '* ', cmd: 'InsertUnorderedList' },
              { start: '- ', cmd: 'InsertUnorderedList' },
              { start: '~', end: '~', cmd: 'createLink' },
            ],
            // advlist_bullet_styles: 'default', // only include square bullets in list
            plugins: [
              // 'textpattern',
              // 'paste',
              'lists',
              'fullscreen',
              'searchreplace',
              // 'advlist', // doesn't support arabic by default
              'autolink',
              'link',
              'visualblocks',
              'code',
              'emoticons',
              'insertdatetime',
              'media',
              'table',
              'help',
              'wordcount',
              'directionality',
            ],
            // toolbar:
            //   'undo redo | formatselect | bold italic | numlist bullist | link emoticons | removeformat',
            automatic_uploads: true,
          }}
          onEditorChange={(val: string) => {
            onChange?.(convertHtmlToMarkdown(val) ?? '');
          }}
        />
        {isError && (
          <div className="absolute top-[101%] right-0 text-[12px] text-red-500">
            <ReturnTranslatedText
              origintext={fieldState?.error?.message}
              text={`general.${fieldState?.error?.message}`}
            />
          </div>
        )}
      </div>
    );
  }
);
